import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Layers, Users, ShieldCheck, Clock, Copy, Folder,
  Settings, Lock, LifeBuoy, ChevronLeft, ChevronRight, LogOut,
  Bell, Search, HelpCircle, X, CheckCircle2, AlertTriangle, AlertCircle, Mail,
  Menu, MessageSquare, Heart, CalendarCheck, FileCheck, ShieldQuestion, Building2,
} from "lucide-react";
import Logo from "../marketing/Logo";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import TrialBanner from "./TrialBanner";

const NAV_TOP = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/app/multi-firm", label: "Master Dashboard", icon: Building2, planFeature: "multi_firm" },
  { to: "/app/flows", label: "Onboarding Flows", icon: Layers },
  { to: "/app/clients", label: "Active Clients", icon: Users },
  { to: "/app/compliance", label: "Compliance Checker", icon: ShieldCheck },
  { to: "/app/aml", label: "AML Screenings", icon: ShieldQuestion },
  { to: "/app/supervision", label: "Supervision", icon: FileCheck, planFeature: "supervision_workflow" },
  { to: "/app/complaints", label: "Complaints", icon: MessageSquare, planFeature: "complaints" },
  { to: "/app/vulnerable-customers", label: "Vulnerable Customers", icon: Heart, planFeature: "vulnerable_customer" },
  { to: "/app/annual-reviews", label: "Annual Reviews", icon: CalendarCheck, planFeature: "annual_reviews" },
  { to: "/app/audit-trail", label: "Audit Trail", icon: Clock },
  { to: "/app/templates", label: "Templates", icon: Copy },
  { to: "/app/documents", label: "Document Library", icon: Folder },
];

const NAV_BOTTOM = [
  { to: "/app/settings", label: "Settings", icon: Settings },
  { to: "/app/settings#security", label: "Security", icon: Lock },
  { to: "/app/help", label: "Help & Support", icon: LifeBuoy },
];

function NotifIcon({ type }) {
  const map = {
    client_invited: { Icon: Mail, color: "#4F46E5" },
    client_started: { Icon: Users, color: "#4F46E5" },
    client_complete: { Icon: CheckCircle2, color: "#10B981" },
    compliance_fail: { Icon: AlertCircle, color: "#EF4444" },
    compliance_review: { Icon: AlertTriangle, color: "#F59E0B" },
    team_joined: { Icon: Users, color: "#4F46E5" },
    trial_expiring: { Icon: AlertTriangle, color: "#F59E0B" },
  };
  const x = map[type] || map.client_invited;
  return <x.Icon size={18} style={{ color: x.color }} />;
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const location = useLocation();
  const nav = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await api.get("/api/notifications");
        setUnread(data.unread || 0);
        setNotifications(data.items || []);
      } catch {}
    };
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [location.pathname]);

  const onNotifClick = async (n) => {
    if (!n.read) {
      await api.post(`/api/notifications/${n.id}/read`).catch(() => {});
      setUnread(Math.max(0, unread - 1));
    }
    if (n.link) nav(n.link);
    setNotifOpen(false);
  };

  const markAllRead = async () => {
    await api.post("/api/notifications/mark-all-read").catch(() => {});
    setUnread(0);
    setNotifications(notifications.map((n) => ({ ...n, read: true })));
  };

  if (!user || user === false) return null;

  const sidebarWidth = collapsed ? 64 : 256;

  const NavItem = ({ to, label, icon: Icon }) => {
    const [toPath, toHash] = to.split("#");
    let active;
    if (toHash) {
      active = location.pathname === toPath && location.hash === `#${toHash}`;
    } else {
      const pathMatches = location.pathname === to || location.pathname.startsWith(to + "/");
      // If the current URL has a hash and another nav item owns it, don't highlight the bare path.
      const hashOwnedByOther = location.hash && [...NAV_TOP, ...NAV_BOTTOM].some(
        (n) => n.to === `${location.pathname}${location.hash}` && n.to !== to
      );
      active = pathMatches && !hashOwnedByOther;
    }
    return (
      <Link
        to={to}
        className={`flex items-center gap-3 h-11 rounded-md transition-colors ${collapsed ? "justify-center px-0" : "px-3"} ${
          active ? "bg-indigo2 text-white" : "text-white/70 hover:text-white hover:bg-navy-800"
        }`}
        title={collapsed ? label : undefined}
        data-testid={`sidebar-${label.toLowerCase().replace(/\s+/g, "-")}`}
      >
        <Icon size={18} className="flex-shrink-0" />
        {!collapsed && <span className="t-body-sm font-medium">{label}</span>}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-surface">
      {/* Sidebar — desktop */}
      <aside
        className="hidden lg:flex fixed top-0 left-0 bottom-0 bg-navy-900 flex-col z-30 transition-all duration-200"
        style={{ width: sidebarWidth }}
        data-testid="app-sidebar"
      >
        <div className={`h-16 flex items-center ${collapsed ? "justify-center" : "px-5"} border-b border-white/5`}>
          {collapsed ? <Logo variant="light" size="sm" /> : <Logo variant="light" />}
        </div>

        <nav className={`flex-1 overflow-y-auto py-4 ${collapsed ? "px-2" : "px-3"} space-y-1`}>
          {NAV_TOP.map((n) => <NavItem key={n.to} {...n} />)}
        </nav>

        <div className={`border-t border-white/5 ${collapsed ? "px-2" : "px-3"} py-3 space-y-1`}>
          {NAV_BOTTOM.map((n) => <NavItem key={n.to} {...n} />)}
        </div>

        <button
          onClick={() => setCollapsed(!collapsed)}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-navy-800 border border-white/10 flex items-center justify-center text-white/70 hover:text-white hover:bg-indigo2"
          aria-label="Toggle sidebar"
          data-testid="sidebar-toggle"
        >
          {collapsed ? <ChevronRight size={14} /> : <ChevronLeft size={14} />}
        </button>
      </aside>

      {/* Mobile nav overlay */}
      {mobileNavOpen && (
        <>
          <div className="lg:hidden fixed inset-0 bg-black/60 z-40" onClick={() => setMobileNavOpen(false)} />
          <aside className="lg:hidden fixed top-0 left-0 bottom-0 bg-navy-900 z-50 w-64 flex flex-col">
            <div className="h-16 flex items-center justify-between px-5 border-b border-white/5">
              <Logo variant="light" />
              <button onClick={() => setMobileNavOpen(false)} className="text-white p-2"><X size={20} /></button>
            </div>
            <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-1" onClick={() => setMobileNavOpen(false)}>
              {NAV_TOP.map((n) => <NavItem key={n.to} {...n} />)}
            </nav>
            <div className="border-t border-white/5 px-3 py-3 space-y-1" onClick={() => setMobileNavOpen(false)}>
              {NAV_BOTTOM.map((n) => <NavItem key={n.to} {...n} />)}
            </div>
          </aside>
        </>
      )}

      {/* Main area */}
      <div className="transition-all duration-200" style={{ paddingLeft: 0, marginLeft: window.innerWidth >= 1024 ? sidebarWidth : 0 }}>
        {/* Topbar */}
        <header className="sticky top-0 z-20 bg-white border-b border-border2 h-16 flex items-center px-5 lg:px-8 gap-4" data-testid="app-topbar">
          <button onClick={() => setMobileNavOpen(true)} className="lg:hidden text-ink p-2" aria-label="Open menu" data-testid="mobile-menu-trigger"><Menu size={22} /></button>

          <div className="flex-1 max-w-md hidden md:block">
            <button
              type="button"
              onClick={() => {
                // Dispatch the Cmd+K shortcut so the global listener picks it up.
                document.dispatchEvent(new KeyboardEvent("keydown", { key: "k", metaKey: true, bubbles: true }));
              }}
              className="relative w-full text-left input2 pl-9 flex items-center"
              style={{ height: 38, fontSize: 14, color: "#94A3B8" }}
              data-testid="global-search"
              aria-label="Open command palette"
            >
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate2" />
              <span>Search clients, flows, checks…</span>
              <span className="absolute right-3 top-1/2 -translate-y-1/2 t-mono text-xs text-slate2 hidden lg:block">⌘K</span>
            </button>
          </div>

          <div className="flex-1 md:flex-none" />

          <button
            onClick={() => setNotifOpen(!notifOpen)}
            className="relative w-10 h-10 rounded-md hover:bg-surface flex items-center justify-center text-slate2 hover:text-ink"
            aria-label="Notifications"
            data-testid="topbar-notifications"
          >
            <Bell size={18} />
            {unread > 0 && (
              <span className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-rose2 text-white text-[10px] font-bold flex items-center justify-center">
                {unread > 9 ? "9+" : unread}
              </span>
            )}
          </button>

          <button className="w-10 h-10 rounded-md hover:bg-surface flex items-center justify-center text-slate2 hover:text-ink" aria-label="Help">
            <HelpCircle size={18} />
          </button>

          <div className="relative">
            <button
              onClick={() => setUserMenuOpen(!userMenuOpen)}
              className="flex items-center gap-2 pl-2 pr-3 h-10 rounded-md hover:bg-surface"
              data-testid="topbar-user-menu"
            >
              <div className="w-8 h-8 rounded-full bg-indigo2 text-white text-sm font-semibold flex items-center justify-center">
                {(user.first_name?.[0] || "") + (user.last_name?.[0] || "")}
              </div>
              <div className="hidden md:block text-left">
                <div className="t-body-sm font-medium leading-none">{user.first_name}</div>
                <div className="text-[10px] text-slate2 uppercase tracking-wider mt-0.5">{user.role.replace("_", " ")}</div>
              </div>
            </button>
            {userMenuOpen && (
              <>
                <div className="fixed inset-0 z-20" onClick={() => setUserMenuOpen(false)} />
                <div className="absolute right-0 top-12 w-60 card2 shadow-card-hover z-30 overflow-hidden animate-slide-up" data-testid="user-menu">
                  <div className="px-4 py-3 bg-surface border-b border-border2">
                    <div className="t-body-sm font-medium">{user.first_name} {user.last_name}</div>
                    <div className="t-caption">{user.email}</div>
                  </div>
                  <Link to="/app/profile" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2.5 t-body-sm hover:bg-surface">My profile</Link>
                  <Link to="/app/settings#security" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2.5 t-body-sm hover:bg-surface">Security settings</Link>
                  <Link to="/app/settings" onClick={() => setUserMenuOpen(false)} className="block px-4 py-2.5 t-body-sm hover:bg-surface">Firm settings</Link>
                  <div className="border-t border-border2" />
                  <button
                    onClick={() => { setUserMenuOpen(false); logout(); nav("/"); }}
                    className="w-full px-4 py-2.5 t-body-sm text-rose2 hover:bg-rose2-50 text-left flex items-center gap-2"
                    data-testid="menu-logout"
                  >
                    <LogOut size={14} /> Sign out
                  </button>
                </div>
              </>
            )}
          </div>
        </header>

        {/* Notification panel */}
        {notifOpen && (
          <>
            <div className="fixed inset-0 bg-black/30 z-30" onClick={() => setNotifOpen(false)} />
            <aside className="fixed top-0 right-0 bottom-0 w-full max-w-sm bg-white z-40 flex flex-col animate-slide-up" data-testid="notification-panel">
              <div className="h-16 px-5 flex items-center justify-between border-b border-border2">
                <h3 className="t-h3">Notifications</h3>
                <div className="flex items-center gap-2">
                  {unread > 0 && (
                    <button onClick={markAllRead} className="t-caption text-indigo2 hover:underline" data-testid="mark-all-read">Mark all read</button>
                  )}
                  <button onClick={() => setNotifOpen(false)} className="p-2 text-slate2 hover:text-ink"><X size={18} /></button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto">
                {notifications.length === 0 ? (
                  <div className="flex flex-col items-center justify-center text-center px-6 py-16">
                    <CheckCircle2 size={48} className="text-emerald2 mb-3" />
                    <h4 className="t-h4 mb-1">You're all caught up</h4>
                    <p className="t-body-sm text-slate2">New notifications will appear here.</p>
                  </div>
                ) : notifications.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => onNotifClick(n)}
                    className={`w-full text-left px-5 py-3 border-b border-border2 hover:bg-surface flex gap-3 ${!n.read ? "bg-indigo2-50/30 border-l-2 border-l-indigo2" : ""}`}
                    data-testid={`notification-${n.id}`}
                  >
                    <NotifIcon type={n.type} />
                    <div className="flex-1 min-w-0">
                      <div className="t-body-sm font-medium text-ink">{n.title}</div>
                      <div className="t-caption truncate">{n.description}</div>
                      <div className="t-caption text-muted2 mt-1">{new Date(n.created_at).toLocaleString()}</div>
                    </div>
                  </button>
                ))}
              </div>
            </aside>
          </>
        )}

        <TrialBanner />
        <main data-testid="app-main" className="px-5 lg:px-8 py-8 pb-24 lg:pb-8">{children}</main>
      </div>

      {/* Mobile bottom navigation (sidebar replacement on mobile per requirement) */}
      <nav
        className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-navy-900 border-t border-white/10 flex justify-around items-center h-16 px-1"
        data-testid="mobile-bottom-nav"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        {NAV_TOP.slice(0, 4).map((n) => {
          const active = location.pathname === n.to || location.pathname.startsWith(n.to + "/");
          return (
            <Link
              key={n.to}
              to={n.to}
              className={`flex flex-col items-center justify-center flex-1 h-full gap-0.5 ${
                active ? "text-white" : "text-white/55"
              }`}
              data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {active && <span className="absolute top-0 w-10 h-0.5 rounded-b bg-indigo2" />}
              <n.icon size={22} />
              <span className="text-[10px] font-semibold tracking-wide">
                {n.label === "Onboarding Flows" ? "Flows" : n.label === "Active Clients" ? "Clients" : n.label === "Compliance Checker" ? "Compliance" : n.label}
              </span>
            </Link>
          );
        })}
        <button
          onClick={() => setMobileNavOpen(true)}
          className={`flex flex-col items-center justify-center flex-1 h-full gap-0.5 text-white/55`}
          data-testid="mobile-nav-more"
        >
          <Menu size={22} />
          <span className="text-[10px] font-semibold tracking-wide">More</span>
        </button>
      </nav>
    </div>
  );
}
